import { takeLatest, put, all, call, select } from 'redux-saga/effects';
import {
  fetchUWData,
  fetchLastCompletedValuation,
  fetchPropertyUWCensusTract,
  fetchPropertyUWContainers,
  fetchPropertyUWImages,
  fetchPropertyUWReloadStatus,
  setSelectedContainer,
  setSelectedContainerId,
  appNotify,
  setPropertyUWClientTxnOpp,
  setIsFetchingContainers,
  setIsFetchingPropertyUWData,
  setIsFetchingLastCompletedValuation,
  updateClientTxnOppsFromQueued,
  setSelectedOpportunityComps,
  setSelectedOpportunityAmherstIndicative,
  setContainers,
  setLastCompletedValuation,
  setTransientValuation,
  setSelectedValuation,
  setSelectedProformaInputsContainer,
  setSelectedOpportunityCensusTracts,
  fetchOpportunitiesForPropertyHistory,
  fetchOpportunitySfhaDetails,
  setExploreViewUnderwriteModalIsOpen,
  setSelectedOpportunityHistory,
  setSelectedOpportunityEventHistory,
  fetchTransactionEvents,
  fetchValuationHistory,
  setSelectedOpportunityValuationHistory,
  setSelectedOpportunityHistoryTab
} from 'store/actions';
import { exploreMapSelector, selectedContainerIdSelector, selectedOpportunitySelector, propertyUWStateSelector } from 'store/selectors'
import { getWithToken, getWithTokenV2 } from 'api/network';
import * as taskUWDataService from 'services/taskUWDataService';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';

export function* getTaskUWDataSaga() {
  
  yield takeLatest(fetchUWData, function* ({ payload }) {
    const { transactionOpportunityId, selectedContainerId = null, history } = payload;
    const txnOpp = yield getTxnOppRawData(transactionOpportunityId);
    const exploreMapState = yield select(exploreMapSelector);
    const isExploreModal = exploreMapState.viewUnderwriteModalIsOpen;
    if(txnOpp && txnOpp.length === 0) {
      history.push(Constants.Routes.HOME)
      yield put(appNotify(`The opportunity ${transactionOpportunityId} was not found`, 'error'));
      return
    }

    if(txnOpp && txnOpp[0].isDeleted) {
      if (isExploreModal) {
        yield put(setExploreViewUnderwriteModalIsOpen(false));
      } else {
        history.push(Constants.Routes.HOME)
      }
      yield put(appNotify(`Opportunity ${transactionOpportunityId} has been deleted`, 'error'));
      return
    }

    try {
      yield put(setIsFetchingPropertyUWData(true));

      yield all([ // Clear out current data
        put(setTransientValuation(null)),
        put(setSelectedValuation(null)),
        put(setSelectedOpportunityComps([])),
        put(setSelectedProformaInputsContainer(null)),
        put(setLastCompletedValuation(null)),
        put(setSelectedOpportunityAmherstIndicative(null)),
        put(setSelectedOpportunityCensusTracts([])),
        put(setSelectedOpportunityHistory(null)),
        put(setSelectedOpportunityEventHistory(null)),
        put(setSelectedOpportunityValuationHistory(null)),
        put(setSelectedOpportunityHistoryTab('opportunities'))
      ]);

      const currentSelectedContainerId = yield select(selectedContainerIdSelector);

      const [fetchedOpp] = yield all([
        call(getLatestClientTxnOppsData, transactionOpportunityId),
        selectedContainerId !== currentSelectedContainerId
          ? put(setSelectedContainerId(selectedContainerId, null, null, transactionOpportunityId, false)) // Pass false to prevent loading flag being turned off by setSelectedContainerId, in case another call takes longer
          : call(fetchUWSnapshotData, transactionOpportunityId, selectedContainerId),
        put(fetchLastCompletedValuation()),
        put(fetchPropertyUWCensusTract(transactionOpportunityId)),
        put(fetchPropertyUWReloadStatus(transactionOpportunityId)),
        put(fetchOpportunitySfhaDetails(transactionOpportunityId))
      ])

      yield put(fetchPropertyUWImages(fetchedOpp));

      yield call(getOppAndEventHistory, fetchedOpp);

      sendClientLog('Info', `Property UW - User fetched property data - txnOpp ${transactionOpportunityId}`);

      yield put(updateClientTxnOppsFromQueued(Constants.SignalRUpdateTarget.PROPERTYUWTXNOPP));
      
      if ([Constants.OpportunityStatus.COUNTER, Constants.OpportunityStatus.DUEDILIGENCE].includes(fetchedOpp.opportunityStatus)) {
        yield put(setSelectedOpportunityHistoryTab('valuations'));
      }
      yield put(setIsFetchingPropertyUWData(false)); 
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
      yield put(setIsFetchingPropertyUWData(false));
    }
  });

  yield takeLatest(fetchLastCompletedValuation, function* () {
    yield put(setIsFetchingLastCompletedValuation(true));
    let lastCompletedValuationData = null;
    try {
      const lastCompletedResponse = yield getWithToken(`/api/underwrite/prop/last`);
      if (lastCompletedResponse.status !== 204) { // No content returned if no underwrite is found
        lastCompletedValuationData = yield lastCompletedResponse.json();
      }
    } catch (error) {
      lastCompletedValuationData = yield error;
    }
    yield put(setLastCompletedValuation(lastCompletedValuationData));
    yield put(setIsFetchingLastCompletedValuation(false));
  });

  yield takeLatest(fetchPropertyUWCensusTract, function* ({ payload: transactionOpportunityId }) {
    yield call(taskUWDataService.getPropertyUWCensusTract, transactionOpportunityId);
  });

  yield takeLatest(fetchPropertyUWImages, function* ({ payload: fetchedOpp }) {
    yield call(taskUWDataService.getPropertyUWImages, fetchedOpp);
  });

  yield takeLatest(fetchPropertyUWReloadStatus, function* ({ payload: transactionOpportunityId }) {
    yield call(taskUWDataService.checkPropertyUWReloadStatus, transactionOpportunityId);
  });

  yield takeLatest(fetchPropertyUWContainers, function* ({ payload: transactionOpportunityId }) {
    yield put(setIsFetchingContainers(true));
    let containers = null;
    try {
      const containersResponse = yield getWithToken(`/api/underwrite/proformaInputsContainer/${transactionOpportunityId}`);
      containers = yield containersResponse.json();
      sendClientLog('Info', `Property UW - User fetched containers - txnOpp ${transactionOpportunityId}`);
    } catch (error) {
      yield put(appNotify(error.message, 'error'));
    }
    yield put(setContainers(containers));
    yield put(setIsFetchingContainers(false));
  });

  yield takeLatest(fetchOpportunitySfhaDetails, function* ({ payload: transactionOpportunityId }) {
    yield call(taskUWDataService.getOpportunitySfhaDetailsSaga, transactionOpportunityId);
  });
}

export function* fetchUWSnapshotDataSaga() {
  yield takeLatest(setSelectedContainerId, function* ({ payload }) {
    const { selectedContainerId, selectedValuationId, transientValuation, transactionOpportunityId, shouldEndLoading = true, callback } = payload;
    const transactionOpportunity = yield select(selectedOpportunitySelector);

    const oppId = transactionOpportunityId ?? transactionOpportunity.id;
    yield call(fetchUWSnapshotData, oppId, selectedContainerId, selectedValuationId, transientValuation, callback);
    if (shouldEndLoading) yield put(setIsFetchingPropertyUWData(false)); 
  });
}

const getUrlWithContainerIdParam = (selectedContainerId) => (baseUrl) => {
  if (!selectedContainerId) {
    return baseUrl
  }

  return baseUrl + `?proformaInputsContainerId=${selectedContainerId}`
}

function* fetchUWSnapshotData(transactionOpportunityId, selectedContainerId, selectedValuationId, transientValuation, callback) {
  const getUrlWithContainerIdParamFunc  = getUrlWithContainerIdParam(selectedContainerId)

  const historicalContainerDataUrl = getUrlWithContainerIdParamFunc(`/api/underwrite/proformaInputsContainer/historicalData/${transactionOpportunityId}`)
  const compsUrl = getUrlWithContainerIdParamFunc(`/api/underwrite/prop/comps/all/${transactionOpportunityId}`)

  try {
    yield put(setIsFetchingPropertyUWData(true));

    const isPostAcqUnderwriteUrl = getUrlWithContainerIdParamFunc(`/api/underwrite/prop/${transactionOpportunityId}/proformainputs/isPostAcquisitionUnderwrite`)

    const isPostAcqUnderwriteResponse = yield getWithToken(isPostAcqUnderwriteUrl);
    const isPostAcqUnderwriteData = yield isPostAcqUnderwriteResponse.json();

    const { isPostAcquisitionUnderwrite } = isPostAcqUnderwriteData

    let proformaUrl = `/api/underwrite/prop/${transactionOpportunityId}/proformainputs`

    if (isPostAcquisitionUnderwrite){
      proformaUrl = proformaUrl + `/postAcquisitions`
    }

    const proformaUrlWithContainerId = getUrlWithContainerIdParamFunc(proformaUrl)

    const [historicalContainerDataResponse, compsData, propertyUnderwriteDto] = yield all([
      getWithToken(historicalContainerDataUrl),
      getWithTokenV2(compsUrl),
      getWithTokenV2(proformaUrlWithContainerId)
    ])

    const historicalContainerData = yield historicalContainerDataResponse.json();
    yield put(setSelectedContainer(historicalContainerData))

    yield put(setSelectedOpportunityComps(compsData));

    const { proformaInputsContainer, amherstIndicative } = propertyUnderwriteDto;
    yield put(setSelectedOpportunityAmherstIndicative(amherstIndicative))
    yield taskUWDataService.getProformaInputsAndValuation(proformaInputsContainer, selectedValuationId, transientValuation, callback)
  } catch (error) {
    yield put(appNotify(error.message, 'error'));
  }
}

// PROPERTY UW
function* getLatestClientTxnOppsData(transactionOpportunityId) {
  try {

    const res = yield getWithToken(`/api/underwrite/workflow/opportunity/${transactionOpportunityId}`);
    const fetchedOpp = yield res.json();

    yield put(setPropertyUWClientTxnOpp(fetchedOpp)); // Update UW txnOpp record with latest
    // yield put(fetchOpportunitiesForPropertyHistory(fetchedOpp.asgPropID));
    
    return fetchedOpp;
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
  }
}

function* getTxnOppRawData(transactionOpportunityId) {
  try {

    const res = yield getWithToken(`/api/underwrite/workflow/transactionOpportunity/${transactionOpportunityId}`);
    if(res.status === 204) {
      return;
    }
    const fetchedOpp = yield res.json();
    return fetchedOpp;
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
  }
}

function* getOppAndEventHistory(fetchedOpp) {
  const { selectedOpportunityHistoryTab } = yield select(propertyUWStateSelector);
  function* oppHistoryCallback(opportunities) {
    yield put(setSelectedOpportunityHistory(opportunities));
  }
  function* eventHistoryCallback(eventHistory) {
    yield put(setSelectedOpportunityEventHistory(eventHistory));
  }
  function* valuationHistoryCallback(valuationHistory) {
    yield put(setSelectedOpportunityValuationHistory(valuationHistory));
    // const humanValuations = valuationHistory.filter(val => val.valuation.user.name !== Constants.Username.AUTOCALC); // Exclude autocalc valuations
    // if (!humanValuations.length && selectedOpportunityHistoryTab !== 'opportunities') {
    //   yield put(setSelectedOpportunityHistoryTab('opportunities'));
    // }
  }
  yield all([
    put(fetchOpportunitiesForPropertyHistory(fetchedOpp.asgPropID, oppHistoryCallback)),
    put(fetchTransactionEvents(fetchedOpp.asgPropID, fetchedOpp.sqft, eventHistoryCallback)),
    put(fetchValuationHistory(fetchedOpp.id, valuationHistoryCallback)),
  ]);
}